import type { ReactNode } from 'react';
import { TONE_HEX, type Tone } from './tones';

/**
 * Flat progress ring for drills and AI jobs: white track between two ink rings, a pastel arc
 * on top, and whatever you put in the middle.
 *
 *   <ProgressRing value={3 / 5} label="Spot 3 of 5">3/5</ProgressRing>
 */
export interface ProgressRingProps {
  /** 0..1, clamped. */
  value: number;
  label: string;
  size?: number;
  stroke?: number;
  tone?: Tone;
  children?: ReactNode;
  className?: string;
}

export function ProgressRing({
  value,
  label,
  size = 72,
  stroke = 12,
  tone = 'lavender',
  children,
  className,
}: ProgressRingProps) {
  const v = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
  const r = (size - stroke) / 2 - 2;
  const c = 2 * Math.PI * r;
  const mid = size / 2;
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(v * 100)}
      className={`relative inline-grid shrink-0 place-items-center ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="absolute inset-0">
        <circle cx={mid} cy={mid} r={r} fill="none" stroke="#000" strokeWidth={stroke + 6} />
        <circle cx={mid} cy={mid} r={r} fill="none" stroke="#fff" strokeWidth={stroke} />
        {v > 0 && (
          <circle
            cx={mid}
            cy={mid}
            r={r}
            fill="none"
            stroke={TONE_HEX[tone]}
            strokeWidth={stroke}
            strokeDasharray={`${c * v} ${c}`}
            strokeLinecap="butt"
            transform={`rotate(-90 ${mid} ${mid})`}
            style={{ transition: 'stroke-dasharray var(--dur-slow) var(--ease-out-expo)' }}
          />
        )}
      </svg>
      <span className="relative font-display text-sm font-black italic leading-none text-ink">{children}</span>
    </span>
  );
}
